import el from "@cypherpotato/el";
import { api } from "../../api";
import { Badge, TagBadge } from "../../components/badge";
import { Button } from "../../components/button";
import { Card, CardContent, CardGrid, CardHeader } from "../../components/card";
import { AreaChart, PieChart } from "../../components/chart";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { FormControl } from "../../components/form";
import { Icon } from "../../components/icon";
import { Column, Content, Row, Section } from "../../components/layout";
import { ExternalLink, Link } from "../../components/link";
import { TabControl } from "../../components/tabcontrol";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableHorizontalBar, TableRow, TableSeparator } from "../../components/table";
import { CopiableText, Heading, InlineCode, MutedText, Paragraph } from "../../components/text";
import { formatCurrency, formatDate, formatDateTime, formatNumber, formatPreciseCurrency, formatVeryPreciseCurrency } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { getUniqueColor } from "../../ui/unique-color";
import { cond, ellipsis, find } from "../../util";
import { DateTimeLabel } from "../../components/datetime-label";
import { DisplayCreditAdditionModal } from "../../helpers/invoice-helpers";
import { ModelBadge } from "../../components/model-icon";
import { RelativeTimeRangePicker } from "../../components/relative-time-range";
import { MonthlyReport } from "../../components/monthly-report";

export default function (ctx) {

    const usageData = el.state.create(null);

    function updateUsageData() {
        runOperation("Loading usage data...", async () => {
            var tmspan = find("timeRange")?.value;
            const result = await api.getJson(`/api/v1/information/usage?timeStart=${tmspan?.start || ""}&timeEnd=${tmspan?.end || ""}`);
            usageData.value = result.data;
        });
    }

    updateUsageData();

    return Dashboard(
        DashboardTitle(
            "Analytics",
            "View advanced account usage metrics, invoices and detailed usage listings."
        ),
        Section(
            { classList: ["analytics-range-controls"] },
            CommandBar(
                FormControl("List to date",
                    RelativeTimeRangePicker({
                        id: "timeRange",
                        onInput() {
                            updateUsageData();
                        }
                    })
                )
            )
        ),
        Section(
            TabControl([
                {
                    title: "Discriminations",
                    contents: el.fragment(
                        Section(
                            el.state.listening([usageData], () =>
                                Content(
                                    usageData.value?.usageDetails.meterGroups.map(meterGroup =>
                                        el.fragment(
                                            Heading(meterGroup.name),
                                            CardGrid(
                                                meterGroup.meters.map(meter =>
                                                    Card(
                                                        CardHeader(meter.description),
                                                        CardContent(el("h2", { style: { margin: 0, fontWeight: 400 }, title: formatNumber(meter.amount) }, meter.value))
                                                    )
                                                )
                                            )
                                        )
                                    )
                                )
                            )
                        ),
                        Section(
                            el.state.listening([usageData], () =>
                                TabControl([
                                    {
                                        title: "Usage per model",
                                        contents: Card(
                                            CardContent(
                                                Section(
                                                    Row(
                                                        Column(
                                                            usageData.value && AreaChart({
                                                                series: usageData.value?.usageDetails.charts.models.series,
                                                                xaxis: {
                                                                    type: "datetime",
                                                                    tickAmount: 4
                                                                },
                                                                yaxis: {
                                                                    show: false,
                                                                    labels: {
                                                                        show: false
                                                                    }
                                                                }
                                                            })
                                                        ),
                                                        Column(
                                                            (() => {
                                                                if (!usageData.value?.usageDetails.modelUsageDiscriminations?.length) return null;

                                                                const grouped = usageData.value.usageDetails.modelUsageDiscriminations
                                                                    .reduce((acc, m) => {
                                                                        acc[m.modelName] = (acc[m.modelName] || 0) + m.totalUsage;
                                                                        return acc;
                                                                    }, {});

                                                                const sorted = Object.entries(grouped)
                                                                    .map(([name, total]) => ({ name, total }))
                                                                    .sort((a, b) => b.total - a.total);

                                                                const top6 = sorted.slice(0, 6);
                                                                const othersTotal = sorted.slice(6).reduce((sum, m) => sum + m.total, 0);

                                                                const labels = top6.map(m => m.name);
                                                                const series = top6.map(m => m.total);

                                                                if (othersTotal > 0) {
                                                                    labels.push("Others");
                                                                    series.push(othersTotal);
                                                                }

                                                                return PieChart({ series, labels, height: 300 });
                                                            })()
                                                        )
                                                    )
                                                ),
                                                Section(
                                                    Table(
                                                        {
                                                            classList: ["hoverable"],
                                                            pagination: {
                                                                perPage: 20
                                                            }
                                                        },
                                                        TableHeader(
                                                            TableHeaderCell("Model name"),
                                                            TableHeaderCell("SKU"),
                                                            TableHeaderCell("Token count"),
                                                            TableHeaderCell("Total usage")
                                                        ),
                                                        TableBody(
                                                            usageData.value?.usageDetails.modelUsageDiscriminations.map(musage =>
                                                                TableRow(
                                                                    TableCell(ModelBadge(musage.modelName)),
                                                                    TableCell(musage.category),
                                                                    TableCell(formatNumber(musage.tokenCount)),
                                                                    TableCell(formatPreciseCurrency(musage.totalUsage), {
                                                                        title: musage.totalUsage
                                                                    })
                                                                )
                                                            )?.nullWhenEmpty() || DefaultRow("There's nothing here.")
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    },
                                    {
                                        title: "Usage per SKU",
                                        contents: Card(
                                            CardContent(
                                                Section(
                                                    usageData.value && AreaChart({
                                                        series: usageData.value?.usageDetails.charts.categories.series,
                                                        xaxis: {
                                                            type: "datetime",
                                                            tickAmount: 4
                                                        },
                                                        yaxis: {
                                                            show: false,
                                                            labels: {
                                                                show: false
                                                            }
                                                        }
                                                    })
                                                ),
                                                Section(
                                                    Table(
                                                        {
                                                            classList: ["hoverable"],
                                                            pagination: {
                                                                perPage: 20
                                                            }
                                                        },
                                                        TableHeader(
                                                            TableHeaderCell("SKU"),
                                                            TableHeaderCell("Quantity"),
                                                            TableHeaderCell("Total usage")
                                                        ),
                                                        TableBody(
                                                            usageData.value?.usageDetails.categoryDiscriminations.map(sku =>
                                                                TableRow(
                                                                    TableCell(sku.description),
                                                                    TableCell(formatNumber(sku.quantity)),
                                                                    TableCell(formatPreciseCurrency(sku.total), {
                                                                        title: sku.total
                                                                    })
                                                                )
                                                            )?.nullWhenEmpty() || DefaultRow("There's nothing here.")
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    },
                                    {
                                        title: "Usage per resource",
                                        contents: Card(
                                            CardContent(
                                                Section(
                                                    usageData.value && AreaChart({
                                                        series: usageData.value?.usageDetails.charts.resources.series,
                                                        xaxis: {
                                                            type: "datetime",
                                                            tickAmount: 4
                                                        },
                                                        yaxis: {
                                                            show: false,
                                                            labels: {
                                                                show: false
                                                            }
                                                        }
                                                    })
                                                ),
                                                Section(
                                                    Table(
                                                        {
                                                            classList: ["hoverable"],
                                                            pagination: {
                                                                perPage: 20
                                                            }
                                                        },
                                                        TableHeader(
                                                            TableHeaderCell("Resource"),
                                                            TableHeaderCell("Total usage")
                                                        ),
                                                        usageData.value?.usageDetails.resourceDiscriminations.map(
                                                            resourceGroup => TableBody(
                                                                TableRow(
                                                                    TableCell(
                                                                        {
                                                                            classList: ["separator"]
                                                                        },
                                                                        cond(
                                                                            [resourceGroup.type[0] == "AiGateway", el.fragment(Icon("route-line"), " AI Gateways")],
                                                                            [resourceGroup.type[0] == "Collection", el.fragment(Icon("book-shelf-line"), " Collections")],
                                                                            [resourceGroup.type[0] == "ChatClient", el.fragment(Icon("chat-1-line"), " Chat clients")],
                                                                            [resourceGroup.type[0] == "ChatClientSession", el.fragment(Icon("chat-quote-line"), " Chat sessions")],
                                                                            [resourceGroup.type[0] == "ApiKey", el.fragment(Icon("key-2-line"), " API keys")]
                                                                        )
                                                                    )
                                                                ),
                                                                ...resourceGroup.items.map(resourceItem =>
                                                                    TableRow(
                                                                        TableCell(
                                                                            resourceItem.name && cond(
                                                                                [resourceGroup.type[0] == "AiGateway", Link(resourceItem.name, `/dashboard/gateways/${resourceItem.id}`)],
                                                                                [resourceGroup.type[0] == "Collection", Link(resourceItem.name, `/dashboard/collections/${resourceItem.id}/documents`)],
                                                                                [resourceGroup.type[0] == "ChatClient", Link(resourceItem.name, `/dashboard/chat-clients/${resourceItem.id}`)],
                                                                                [resourceGroup.type[0] == "ChatClientSession", Link(ellipsis(resourceItem.name, 20), `/chat/${resourceItem.name}`)],
                                                                                [resourceGroup.type[0] == "ApiKey", Link(resourceItem.name, `/dashboard/account`)]
                                                                            ) || MutedText("Other", {
                                                                                title: "Represents removed, unspecified, or unknown entities."
                                                                            })
                                                                        ),
                                                                        TableCell(formatPreciseCurrency(resourceItem.total), {
                                                                            title: resourceItem.total
                                                                        })
                                                                    )
                                                                )
                                                            )
                                                        )?.nullWhenEmpty() || DefaultRow("There's nothing here.")
                                                    )
                                                )
                                            )
                                        )
                                    }
                                ])
                            ),
                        )
                    )
                },
                {
                    title: "Monthly Reports",
                    contents: MonthlyReport("/api/v1/information/usage/monthly-report")
                },
                {
                    title: "Listings",
                    contents: el.fragment(
                        Section(
                            Heading(
                                "Latest usage listings",
                                Paragraph("Below are the latest balance releases in your account."),
                                Paragraph("This data may take up to 10 minutes to update.")
                            )
                        ),
                        Section(
                            Card({ classList: ["bg-card"] },
                                CardHeader("Usage per requests"),
                                CardContent(
                                    Table(
                                        {
                                            classList: ["hoverable"],
                                            pagination: {
                                                perPage: 50
                                            }
                                        },
                                        TableHeader(
                                            TableHeaderCell("Id"),
                                            TableHeaderCell("Request Id"),
                                            TableHeaderCell("Moment"),
                                            TableHeaderCell("Description"),
                                            TableHeaderCell("SKU"),
                                            TableHeaderCell("Quantity"),
                                            TableHeaderCell("Amount"),
                                        ),
                                        el.state.listening([usageData], () =>
                                            TableBody(
                                                usageData.value?.usageDetails.dayListings.map(dayListing =>
                                                    (() => {
                                                        let requestCarry = 0
                                                        let lastRequestId = null
                                                        return el.fragment(
                                                            TableSeparator(formatDate(dayListing.day)),
                                                            ...dayListing.items.flatMap((item, idx, arr) => {
                                                                const rows = []
                                                                const newGroup = item.requestId !== lastRequestId

                                                                // Fechar grupo anterior
                                                                if (newGroup && lastRequestId !== null) {
                                                                    rows.push(
                                                                        TableHorizontalBar()
                                                                    )
                                                                    requestCarry = 0
                                                                }

                                                                if (newGroup) {
                                                                    lastRequestId = item.requestId
                                                                }

                                                                requestCarry += item.amount

                                                                rows.push(
                                                                    TableRow(
                                                                        TableCell(
                                                                            CopiableText('#' + item.id.substr(-4), item.id)
                                                                        ),
                                                                        TableCell(
                                                                            TagBadge(
                                                                                CopiableText('#' + item.requestId.substr(-4), item.requestId),
                                                                                { style: { backgroundColor: getUniqueColor(item.requestId), color: 'white' } }
                                                                            )
                                                                        ),
                                                                        TableCell(formatDateTime(item.moment), { title: item.moment }),
                                                                        TableCell(item.description || MutedText("Empty")),
                                                                        TableCell(
                                                                            item.category
                                                                                ? InlineCode(item.category)
                                                                                : MutedText("Empty")
                                                                        ),
                                                                        TableCell(formatNumber(item.quantity)),
                                                                        TableCell(
                                                                            formatPreciseCurrency(item.amount),
                                                                            {
                                                                                title: `Unit price x1000: ${formatVeryPreciseCurrency(item.unitPrice * 1000)}, total: ${formatVeryPreciseCurrency(item.amount)}`
                                                                            }
                                                                        )
                                                                    )
                                                                )

                                                                const groupEnds = idx === arr.length - 1 || arr[idx + 1].requestId !== item.requestId
                                                                if (groupEnds) {
                                                                    rows.push(
                                                                        TableRow(
                                                                            TableCell(MutedText("Request total = "), { colspan: 6, style: { textAlign: "right" } }),
                                                                            TableCell(formatPreciseCurrency(requestCarry), { title: requestCarry }),
                                                                            { style: { borderTop: "2px solid var(--border-color)" } }
                                                                        )
                                                                    )
                                                                }

                                                                return rows
                                                            })
                                                        )
                                                    })()
                                                )?.nullWhenEmpty() || DefaultRow("There's nothing here."),
                                                {
                                                    mount() {
                                                        this.closest('table').refreshPagination()
                                                    }
                                                }
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                },
                {
                    title: "Invoices",
                    contents: el.fragment(
                        Section(
                            Heading(
                                "Invoices",
                                Paragraph("To add credits to your account, click the button bellow.")
                            ),
                            CommandBar(
                                Button(Icon("add-line"), "Add credits", {
                                    onClick() {
                                        DisplayCreditAdditionModal()
                                    }
                                })
                            )
                        ),
                        Section(
                            Card(
                                { classList: ["bg-card"] },
                                CardHeader(
                                    "My invoices"
                                ),
                                CardContent(
                                    Table(
                                        {
                                            classList: ["hoverable"]
                                        },
                                        TableHeader(
                                            TableHeaderCell("Invoice"),
                                            TableHeaderCell("Date"),
                                            TableHeaderCell("Expiration"),
                                            TableHeaderCell("Description"),
                                            TableHeaderCell("Type"),
                                            TableHeaderCell("Amount"),
                                            TableHeaderCell("Status"),
                                            TableHeaderActionCell()
                                        ),
                                        el.state.listening([usageData], () =>
                                            TableBody(
                                                usageData.value?.invoices.map(invoice =>
                                                    TableRow(
                                                        TableCell(
                                                            CopiableText("#" + invoice.invoiceId.substr(-4), invoice.invoiceId)
                                                        ),
                                                        TableCell(DateTimeLabel(invoice.createdAt)),
                                                        TableCell(
                                                            invoice.expiresAt && formatDateTime(invoice.expiresAt) || MutedText("-"),
                                                            invoice.isExpired && el.fragment(" ", Badge("Expired", "var(--warn)"))
                                                        ),
                                                        TableCell(invoice.description?.nullWhenEmpty() || MutedText("None")),
                                                        TableCell(invoice.type === "CreditAddition" ? Badge("Credits") : Badge("Subscription")),
                                                        TableCell(formatCurrency(invoice.amount)),
                                                        TableCell(invoice.paidAt == null ? Badge("Not paid", "var(--warn)") : Badge("Paid", "var(--success)")),
                                                        TableActionCell(
                                                            invoice.paymentLocation && ExternalLink("Pay", invoice.paymentLocation),
                                                            invoice.receiptLocation && ExternalLink("Receipt", invoice.receiptLocation)
                                                        )
                                                    )
                                                )?.nullWhenEmpty() || DefaultRow("There's nothing here.")
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                }
            ], {
                type: "fixed"
            })
        )
    )
}
